import React, { type ReactNode, useCallback, useRef } from 'react';

import AnalyticsWebClient from '@atlassiansox/analytics-web-client';

import { AnalyticsListener } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	ANALYTICS_BRIDGE_CHANNEL,
	extractAWCDataFromEvent,
	OPERATIONAL_EVENT_TYPE,
	SCREEN_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	UI_EVENT_TYPE,
} from '@atlassian/analytics-bridge';

import { ERROR_IFRAME_SOURCE_CHAR_LIMIT_EXCEEDED } from './constants';

const analyticsClientInfo = {
	env: process.env.NODE_ENV !== 'production' ? 'staging' : 'prod',
	product: 'jira',
};

export type AvailableSite = {
	avatarUrl: string;
	cloudId: string;
	displayName: string;
	isVertigo: boolean;
	products: AvailableSitesProductType[];
	url: string;
};

export type AvailableSitesResponse = {
	sites: AvailableSite[];
};
export type AvailableSitesProductType =
	| 'jira-core.ondemand'
	| 'jira-incident-manager.ondemand'
	| 'jira-product-discovery'
	| 'jira-servicedesk.ondemand'
	| 'jira-software.ondemand';

const LIGHT_MODE = 'light' as const;
const DARK_MODE = 'dark' as const;
const AUTO = 'auto' as const;
const AVAILABLE_SITES_PATH = '/gateway/api/available-sites';
const AVAILABLE_SITES_UNIT_COMPLIANT_PATH = '/gateway/api/experimental/available-sites';
export type ThemeMode = typeof DARK_MODE | typeof LIGHT_MODE | typeof AUTO | undefined;

// Server side cannot handle a url over 2048 characters
export const MAX_CHARACTERS_PER_URL = 2048;

export type GicConfigurationProps = {
	/**
	 * removes project selector
	 */
	disableProjectDropdown?: boolean;
	/**
	 * Keeps only the required fields in the GIC embed
	 */
	displayOnlyRequiredFields?: boolean;
	/**
	 * removes site selector
	 * @default true
	 */
	displaySitePicker?: boolean;
};

// Adapted from function of the same name from `@atlassian/link-create-jira`
// This gets the Issue Create embed URL for the given domain name and params
export const getIframeSrc = ({
	colorMode = AUTO,
	domainName = '',
	overrideCloudId,
	params,
	onFailure,
	displaySitePicker,
	disableProjectDropdown,
	displayOnlyRequiredFields,
}: {
	colorMode?: ThemeMode;
	domainName?: string;
	onFailure?: (error: unknown) => void;
	overrideCloudId?: string;
	params?: URLSearchParams | Record<string, string>;
} & GicConfigurationProps): string => {
	const iframeParams = new URLSearchParams(params);
	iframeParams.append('postMessageInit', '1');

	if (displaySitePicker === false) {
		iframeParams.append('displaySitePicker', 'false');
	}
	if (disableProjectDropdown) {
		iframeParams.append('disableProjectDropdown', 'true');
	}
	if (displayOnlyRequiredFields) {
		iframeParams.append('displayOnlyRequiredFields', 'true');
	}
	if (colorMode) {
		iframeParams.append('themeMode', colorMode);
	}
	if (overrideCloudId) {
		iframeParams.append('overrideCloudId', overrideCloudId);
	}
	/**
	 * We want to use the postMessageInit param to instruct the Jira embed iframe
	 * to wait before mounting Issue Create.
	 * It will send a message back to us when it's ready to receive the init message
	 */
	const srcUrl = `${domainName}/jira/issues/create/embed?${iframeParams}`;

	if (srcUrl.length > MAX_CHARACTERS_PER_URL) {
		// Will need to handle this case properly in the future, only logging a warning for now
		if (process.env.NODE_ENV !== 'production') {
			/* eslint-disable-next-line no-console */
			console.warn(ERROR_IFRAME_SOURCE_CHAR_LIMIT_EXCEEDED);
		}
		onFailure?.(new Error(ERROR_IFRAME_SOURCE_CHAR_LIMIT_EXCEEDED));
	}

	return srcUrl;
};

export const JIRA_PRODUCTS: AvailableSitesProductType[] = [
	'jira-software.ondemand',
	'jira-core.ondemand',
	'jira-servicedesk.ondemand',
	'jira-product-discovery',
	// 'jira-incident-manager.ondemand',
];

export async function getAvailableJiraSites(
	baseUrlForAvailableSites?: string,
): Promise<AvailableSitesResponse> {
	const requestConfig = {
		method: 'POST',
		credentials: 'include' as RequestCredentials,
		headers: {
			Accept: 'application/json',
			'Cache-Control': 'no-cache',
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			products: JIRA_PRODUCTS,
		}),
	};

	const base = baseUrlForAvailableSites ? baseUrlForAvailableSites.replace(/\/$/, '') : '';
	const availableSitesPath= fg('issue_create_modal_site_picker_api_unit_compliant') ? AVAILABLE_SITES_UNIT_COMPLIANT_PATH : AVAILABLE_SITES_PATH;
	const response = await fetch(`${base}${availableSitesPath}`, requestConfig);
	if (response.ok) {
		return response.json();
	}
	throw response;
}

export const AnalyticsProvider = (props: { children: ReactNode }): React.JSX.Element => {
	const analyticsClient = useRef<AnalyticsWebClient>(
		new AnalyticsWebClient(analyticsClientInfo, {}),
	);

	const sendEvent = useCallback((event: { payload: Record<string, any>; type: string }) => {
		const { type, payload } = event;
		switch (type) {
			case UI_EVENT_TYPE:
				// @ts-expect-error - Argument of type 'Record<string, any>' is not assignable to parameter of type 'UITrackAndOperationalEventPayload'
				analyticsClient.current.sendUIEvent(payload);
				break;
			case TRACK_EVENT_TYPE:
				// @ts-expect-error - Argument of type 'Record<string, any>' is not assignable to parameter of type 'UITrackAndOperationalEventPayload'
				analyticsClient.current.sendTrackEvent(payload);
				break;
			case OPERATIONAL_EVENT_TYPE:
				// @ts-expect-error - Argument of type 'Record<string, any>' is not assignable to parameter of type 'UITrackAndOperationalEventPayload'
				analyticsClient.current.sendOperationalEvent(payload);
				break;
			case SCREEN_EVENT_TYPE:
				analyticsClient.current.sendScreenEvent(payload.name, null, payload.attributes);
				break;
			default:
				break;
		}
	}, []);

	return (
		<AnalyticsListener
			channel={ANALYTICS_BRIDGE_CHANNEL}
			onEvent={(event) => sendEvent(extractAWCDataFromEvent(event))}
		>
			{props.children}
		</AnalyticsListener>
	);
};

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations.
 *
 * @param {Function} func The function to debounce.
 * @param {number} wait The number of milliseconds to delay.
 * @returns {Function} Returns the new debounced function.
 */
export const debounce = <T extends (...args: any[]) => any>(
	func: T,
	wait: number,
): T & { cancel?: () => void } => {
	let timeout: ReturnType<typeof setTimeout> | undefined;

	if (!fg('jira_ai_fix_aiwc_duplicated_analytics')) {
		return func;
	}

	const debounced = (...args: Parameters<T>) => {
		const later = () => {
			timeout = undefined;
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};

	debounced.cancel = () => {
		clearTimeout(timeout);
	};

	return debounced as T & { cancel?: () => void };
};
